// iter526 — Lipid Tracker Page
// 7-day AIP/TG/LDL/HDL trend + cardio symptom log + Coach Nudge.
//
// Route: /dashboard/lipid-tracker
// Sources:
//   GET    /api/lipid-tracker/trend?days=7
//   POST   /api/lipid-tracker/symptom
//   GET    /api/lipid-tracker/symptoms
//   DELETE /api/lipid-tracker/symptoms/{id}
//   GET    /api/lipid-tracker/coach-nudge

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import {
  ArrowLeft, HeartPulse, Bell, Loader2, Plus, X,
  Sparkles, Droplets, Activity, AlertTriangle, TrendingDown,
} from "lucide-react";

const QUICK_SYMPTOMS = [
  "palpitations", "orthostatic", "chest_pain", "dyspnea",
  "leg_swelling", "claudication", "muscle_cramps", "cold_extremities",
];

const TIER = {
  low_risk:      { fg: "#10B981", bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.30)" },
  moderate_risk: { fg: "#F59E0B", bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.30)" },
  high_risk:     { fg: "#EF4444", bg: "rgba(239,68,68,0.10)",  border: "rgba(239,68,68,0.30)" },
};

function tierForAip(idx) {
  if (idx >= 0.21) return TIER.high_risk;
  if (idx >= 0.11) return TIER.moderate_risk;
  return TIER.low_risk;
}

export default function LipidTrackerPage() {
  const { API, user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const bg = language === "bg";

  const [trend, setTrend] = useState(null);
  const [symptoms, setSymptoms] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [nudge, setNudge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [severity, setSeverity] = useState(2);

  const fetchAll = async () => {
    try {
      const [t, s, n] = await Promise.all([
        axios.get(`${API}/lipid-tracker/trend?days=7`),
        axios.get(`${API}/lipid-tracker/symptoms?days=7`),
        axios.get(`${API}/lipid-tracker/coach-nudge`),
      ]);
      setTrend(t.data);
      setSymptoms(s.data?.events || []);
      setCatalog(s.data?.catalog || {});
      setNudge(n.data?.nudge || null);
    } catch { /* soft fail */ } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!user) return;
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const todaySymptoms = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return symptoms.filter((s) => (s.occurred_at_date || "") === today);
  }, [symptoms]);

  const logSymptom = async (key) => {
    try {
      const { data } = await axios.post(`${API}/lipid-tracker/symptom`, {
        symptom_key: key, severity,
      });
      setSymptoms((p) => [data, ...p]);
      toast.success(bg ? `Логнат: ${data.display_bg}` : `Logged: ${data.display_en}`);
      const t = await axios.get(`${API}/lipid-tracker/trend?days=7`);
      setTrend(t.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    }
  };

  const deleteSymptom = async (id) => {
    try {
      await axios.delete(`${API}/lipid-tracker/symptoms/${id}`);
      setSymptoms((p) => p.filter((s) => s.id !== id));
    } catch { /* */ }
  };

  if (!user) return null;

  const series = trend?.series || [];
  // For chart we plot AIP_index (-0.3..+0.6), mapped to 0..100% bar height.
  const maxAbs = Math.max(0.3, ...series.map((s) => Math.abs(s.aip_index ?? 0)));
  const tierStyle = TIER[trend?.tier || "low_risk"];

  return (
    <div
      data-testid="lipid-tracker-page"
      className={`min-h-screen ${isDark ? "bg-slate-950 text-white" : "bg-slate-50 text-slate-900"}`}
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/dashboard")}
            data-testid="lipid-back-btn"
            className={`w-9 h-9 rounded-xl flex items-center justify-center transition ${
              isDark ? "bg-slate-900 border border-slate-800 hover:bg-slate-800"
                     : "bg-white border border-slate-200 hover:bg-slate-100"
            }`}
            aria-label={bg ? "Назад" : "Back"}
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1">
            <h1 className={`text-xl sm:text-2xl font-black tracking-tight`}>
              {bg ? "Липиден трекер (AIP)" : "Lipid Tracker (AIP)"}
            </h1>
            <p className={`text-[12px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {bg
                ? "Атерогенен индекс на плазмата + TG/LDL/HDL/ApoB по дни — кардио-симптом лог"
                : "Atherogenic Index of Plasma + TG/LDL/HDL/ApoB by day — cardio symptom log"}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin opacity-60" />
          </div>
        ) : (
          <>
            {nudge && (
              <div
                data-testid="lipid-coach-nudge"
                className={`rounded-2xl p-4 border-2 ${
                  isDark ? "bg-rose-500/10 border-rose-500/40" : "bg-rose-50 border-rose-300"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                    isDark ? "bg-rose-500/20 text-rose-300" : "bg-rose-100 text-rose-700"
                  }`}>
                    <Bell className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-[13px] font-black ${isDark ? "text-rose-200" : "text-rose-800"}`}>
                      {bg ? nudge.title_bg : nudge.title_en}
                    </div>
                    <p className={`text-[12px] mt-1 leading-relaxed ${isDark ? "text-rose-200/90" : "text-rose-900/90"}`}>
                      {bg ? nudge.message_bg : nudge.message_en}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Hero AIP */}
            <div
              data-testid="lipid-hero"
              className={`rounded-3xl p-5 sm:p-6 border ${
                isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"
              }`}
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <div className={`text-[10px] uppercase tracking-widest font-bold ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {bg ? "Среден AIP за 7 дни" : "7-day mean AIP"}
                  </div>
                  <div className="flex items-end gap-3 mt-1">
                    <div className="text-5xl sm:text-6xl font-black tracking-tight"
                         style={{ color: tierStyle.fg }} data-testid="lipid-weekly-aip">
                      {trend?.aip_avg ?? 0}
                    </div>
                    <span
                      data-testid="lipid-weekly-tier"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-2"
                      style={{ color: tierStyle.fg, backgroundColor: tierStyle.bg, border: `1px solid ${tierStyle.border}` }}
                    >
                      <HeartPulse className="w-3 h-3" />
                      {bg ? trend?.tier_bg : (trend?.tier || "").replace("_", " ")}
                    </span>
                  </div>
                  <p className={`text-[11.5px] mt-1.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {bg
                      ? `Висок риск в ${trend?.elevated_days_count ?? 0} от 7 дни · ${trend?.total_meals_in_window ?? 0} ястия`
                      : `High risk on ${trend?.elevated_days_count ?? 0} of 7 days · ${trend?.total_meals_in_window ?? 0} meals`}
                  </p>
                </div>
              </div>

              {/* AIP bar chart (line of bars) */}
              <div className="mt-3">
                <div className="flex items-end gap-1.5 h-32" data-testid="lipid-trend-chart">
                  {series.map((d, i) => {
                    const aip = d.aip_index ?? 0;
                    const h = Math.max(6, (Math.abs(aip) / maxAbs) * 100);
                    const dayStyle = tierForAip(aip);
                    const today = i === series.length - 1;
                    return (
                      <div key={d.date} className="flex-1 flex flex-col items-center gap-1" data-testid={`lipid-trend-day-${i}`}>
                        <div className={`text-[9px] font-bold ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                          {aip}
                        </div>
                        <div
                          className={`w-full rounded-md transition-all duration-700 relative ${today ? "ring-2 ring-offset-1" : ""}`}
                          style={{
                            height: `${h}%`,
                            backgroundColor: dayStyle.fg,
                            opacity: today ? 1 : 0.75,
                            "--tw-ring-color": dayStyle.fg,
                          }}
                          title={`${d.date}: AIP ${aip} · ${d.meal_count} meals · ${d.symptom_count} symptoms`}
                        >
                          {d.symptom_count > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full bg-rose-500 text-white text-[8px] font-bold flex items-center justify-center">
                              {d.symptom_count}
                            </span>
                          )}
                        </div>
                        <div className={`text-[9px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                          {new Date(d.date).toLocaleDateString(bg ? "bg-BG" : "en-US", { weekday: "short" }).slice(0,2)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className={`text-[9px] text-center mt-2 italic ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                  {bg
                    ? "AIP < 0.11 нисък · 0.11-0.21 умерен · ≥ 0.21 висок риск (Dobiasova 2001, PMID 11533068)"
                    : "AIP < 0.11 low · 0.11-0.21 moderate · ≥ 0.21 high risk"}
                </div>
              </div>

              {/* Best/Worst */}
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div className={`rounded-xl p-2 ${isDark ? "bg-emerald-500/10" : "bg-emerald-50"}`}>
                  <div className={`text-[9px] uppercase tracking-wider font-bold ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                    {bg ? "Най-добър ден" : "Best day"}
                  </div>
                  <div className={`text-base font-extrabold ${isDark ? "text-emerald-200" : "text-emerald-800"}`}>
                    {trend?.best_aip}
                  </div>
                </div>
                <div className={`rounded-xl p-2 ${isDark ? "bg-rose-500/10" : "bg-rose-50"}`}>
                  <div className={`text-[9px] uppercase tracking-wider font-bold ${isDark ? "text-rose-300" : "text-rose-700"}`}>
                    {bg ? "Най-лош ден" : "Worst day"}
                  </div>
                  <div className={`text-base font-extrabold ${isDark ? "text-rose-200" : "text-rose-800"}`}>
                    {trend?.worst_aip}
                  </div>
                </div>
              </div>
            </div>

            {/* Per-axis 7-day mini-chart */}
            <div
              data-testid="lipid-axes-mini"
              className={`rounded-3xl p-4 sm:p-5 border ${
                isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"
              }`}
            >
              <h3 className={`text-[14px] font-black mb-3 ${isDark ? "text-white" : "text-slate-900"}`}>
                {bg ? "TG / LDL / HDL / ApoB по дни" : "TG / LDL / HDL / ApoB by day"}
              </h3>
              <div className="grid grid-cols-7 gap-1">
                {series.map((d, i) => (
                  <div key={d.date} className="space-y-1" data-testid={`lipid-axes-day-${i}`}>
                    <div className="flex flex-col gap-0.5">
                      {[
                        { k: "tg", v: d.tg_percent, color: "#EF4444" },
                        { k: "ldl", v: d.ldl_percent, color: "#F59E0B" },
                        { k: "hdl", v: d.hdl_percent, color: "#10B981" },
                        { k: "apob", v: d.apob_percent, color: "#A855F7" },
                      ].map((row) => (
                        <div key={row.k} className={`h-1.5 rounded-full overflow-hidden ${isDark ? "bg-white/10" : "bg-slate-100"}`}>
                          <div className="h-full" style={{ width: `${Math.min(100, row.v)}%`, backgroundColor: row.color }} />
                        </div>
                      ))}
                    </div>
                    <div className={`text-[9px] text-center ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                      {new Date(d.date).getDate()}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mt-2 text-[10px]">
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500" /> TG</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> LDL</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> HDL</span>
                <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-500" /> ApoB</span>
              </div>
            </div>

            {/* Symptom log */}
            <div
              data-testid="lipid-symptom-card"
              className={`rounded-3xl p-4 sm:p-5 border ${
                isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-[14px] font-black ${isDark ? "text-white" : "text-slate-900"}`}>
                  {bg ? "Кардио-симптом лог" : "Cardio symptom log"}
                </h3>
                <div className="flex items-center gap-1">
                  <span className={`text-[10px] uppercase tracking-wider font-bold mr-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {bg ? "тежест" : "severity"}
                  </span>
                  {[1, 2, 3].map((s) => (
                    <button
                      key={s} onClick={() => setSeverity(s)}
                      data-testid={`lipid-severity-${s}`}
                      className={`w-7 h-7 rounded-lg text-[11px] font-bold transition ${
                        severity === s ? "bg-rose-500 text-white"
                          : isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-700"
                      }`}
                    >{s}</button>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-3" data-testid="lipid-symptom-chips">
                {QUICK_SYMPTOMS.map((key) => {
                  const c = catalog[key]; if (!c) return null;
                  return (
                    <button
                      key={key} onClick={() => logSymptom(key)}
                      data-testid={`lipid-quick-${key}`}
                      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[11px] font-semibold transition active:scale-[0.97] ${
                        isDark ? "bg-rose-500/10 text-rose-200 border border-rose-500/30 hover:bg-rose-500/20"
                               : "bg-rose-50 text-rose-800 border border-rose-200 hover:bg-rose-100"
                      }`}
                    ><Plus className="w-3 h-3" />{bg ? c.bg : c.en}</button>
                  );
                })}
              </div>

              <div className={`text-[10px] font-bold uppercase tracking-widest mb-1.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {bg ? "Днес" : "Today"}
                {todaySymptoms.length > 0 && (
                  <span className={`ml-2 px-1.5 py-0.5 rounded-full ${isDark ? "bg-rose-500/15 text-rose-300" : "bg-rose-100 text-rose-700"}`}>
                    {todaySymptoms.length}
                  </span>
                )}
              </div>
              {todaySymptoms.length === 0 ? (
                <div className={`text-[11.5px] italic ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                  {bg ? "Няма логнати симптоми днес." : "No symptoms logged today."}
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="lipid-today-symptoms">
                  {todaySymptoms.map((s) => (
                    <div key={s.id} className={`group inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border text-[11px] ${
                      isDark ? "bg-slate-950/60 border-slate-800 text-slate-200" : "bg-slate-50 border-slate-200 text-slate-800"
                    }`}>
                      <span className="font-bold">{bg ? s.display_bg : s.display_en}</span>
                      <span className="opacity-60">· {s.severity}/3</span>
                      <button onClick={() => deleteSymptom(s.id)} className="opacity-0 group-hover:opacity-100 transition"
                              data-testid={`lipid-delete-${s.id}`}><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recommendations */}
            <div
              data-testid="lipid-recommendations"
              className={`rounded-3xl p-4 sm:p-5 border ${
                isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"
              }`}
            >
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-rose-400" />
                <h3 className={`text-[14px] font-black ${isDark ? "text-white" : "text-slate-900"}`}>
                  {bg ? "Препоръки за днес" : "Today's interventions"}
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className={`rounded-xl p-3 ${isDark ? "bg-slate-950/60" : "bg-slate-50"}`}>
                  <Droplets className={`w-4 h-4 mb-1.5 ${isDark ? "text-cyan-300" : "text-cyan-600"}`} />
                  <div className={`text-[12px] font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                    {bg ? "Омега-3 EPA/DHA" : "Omega-3 EPA/DHA"}
                  </div>
                  <div className={`text-[11px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                    {bg ? "2g дневно, с храна — намалява TG до 30%" : "2g daily with meal — cuts TG up to 30%"}
                  </div>
                </div>
                <div className={`rounded-xl p-3 ${isDark ? "bg-slate-950/60" : "bg-slate-50"}`}>
                  <TrendingDown className={`w-4 h-4 mb-1.5 ${isDark ? "text-violet-300" : "text-violet-600"}`} />
                  <div className={`text-[12px] font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                    {bg ? "Бергамот / Червен ферм. ориз" : "Bergamot / Red yeast rice"}
                  </div>
                  <div className={`text-[11px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                    {bg ? "↓LDL/ApoB — обсъди с лекар при статин терапия" : "↓LDL/ApoB — discuss with MD if on statins"}
                  </div>
                </div>
                <div className={`rounded-xl p-3 ${isDark ? "bg-slate-950/60" : "bg-slate-50"}`}>
                  <Activity className={`w-4 h-4 mb-1.5 ${isDark ? "text-emerald-300" : "text-emerald-600"}`} />
                  <div className={`text-[12px] font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                    {bg ? "Фибри + движение" : "Fiber + movement"}
                  </div>
                  <div className={`text-[11px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                    {bg ? "Псилиум 10g, авокадо, 30 мин бърза разходка" : "Psyllium 10g, avocado, 30-min brisk walk"}
                  </div>
                </div>
              </div>
            </div>

            <div className={`text-[10px] italic px-2 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
              <AlertTriangle className="inline w-2.5 h-2.5 mr-1 align-text-bottom" />
              {bg
                ? "Образователен модул — не замества лабораторен липиден профил (ApoB, LDL-c, TG, HDL-c, Lp(a)) или клинична оценка. PMID 11533068, 24847203, 27355532."
                : "Educational module — does not replace lab lipid panel or clinical evaluation."}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
